import { useEffect, useState, useCallback } from "react";
import AdminHeader from "../components/admin/AdminHeader";
import { Link } from "react-router-dom";
import {
  ArrowLeft, ShieldCheck, Loader2, Camera,
  CheckCircle2, XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
  fetchAdminPhotoSubmissions,
  approvePhotoSubmission,
  rejectPhotoSubmission,
} from "../lib/api";
import { formatApiError } from "../lib/apiError";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ADMIN_TOKEN_KEY = "medskin_admin_token";

const STATUS_TONE = {
  PENDING:  "bg-amber-50 border-amber-200 text-amber-800",
  APPROVED: "bg-emerald-50 border-emerald-200 text-emerald-800",
  REJECTED: "bg-red-50 border-red-200 text-red-800",
};

function StatTile({ label, value, tone }) {
  const TONES = {
    amber:  "bg-amber-50 border-amber-200 text-amber-900",
    green:  "bg-emerald-50 border-emerald-200 text-emerald-900",
    red:    "bg-red-50 border-red-200 text-red-900",
  };
  return (
    <div
      className={`rounded-xl border p-3 text-center ${TONES[tone]}`}
      data-testid={`photo-summary-${label.toLowerCase()}`}
    >
      <div className="text-[10px] uppercase tracking-wider opacity-70 font-bold">{label}</div>
      <div className="text-2xl font-bold tabular-nums mt-0.5">{value ?? 0}</div>
    </div>
  );
}

/**
 * <img> can't send Authorization headers, so for PENDING submissions we
 * fetch the bytes via the authenticated XHR path and render via blob URL.
 * Approved images are public — render the URL directly.
 */
function AuthedThumbnail({ submissionId, kind, status, alt }) {
  const [src, setSrc] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let revoked = false;
    let blobUrl = null;

    if (status === "APPROVED") {
      setSrc(`${BACKEND}/api/photo-submissions/${submissionId}/${kind}`);
      return () => {};
    }

    const token = localStorage.getItem(ADMIN_TOKEN_KEY);
    fetch(`${BACKEND}/api/photo-submissions/${submissionId}/${kind}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(async (resp) => {
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const blob = await resp.blob();
        if (revoked) return;
        blobUrl = URL.createObjectURL(blob);
        setSrc(blobUrl);
      })
      .catch(() => setError(true));

    return () => {
      revoked = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [submissionId, kind, status]);

  if (error) {
    return (
      <div
        data-testid={`photo-thumb-error-${submissionId}-${kind}`}
        className="w-full h-40 rounded-lg bg-stone-100 border border-stone-200 flex items-center justify-center text-[11px] text-stone-500"
      >
        Couldn't load
      </div>
    );
  }
  if (!src) {
    return (
      <div
        data-testid={`photo-thumb-loading-${submissionId}-${kind}`}
        className="w-full h-40 rounded-lg bg-stone-100 border border-stone-200 flex items-center justify-center"
      >
        <Loader2 className="h-4 w-4 animate-spin text-stone-400" />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      data-testid={`photo-thumb-${submissionId}-${kind}`}
      className="block w-full h-40 object-contain rounded-lg bg-white border border-stone-200"
    />
  );
}

function PhotoSubmissionCard({ sub, onAction }) {
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState("");
  const [setCanonical, setSetCanonical] = useState(true);
  const [queueOcr, setQueueOcr] = useState(true);

  const isPending = sub.status === "PENDING";
  const tone = STATUS_TONE[sub.status] || STATUS_TONE.PENDING;
  const hasIngredients = !!sub.ingredients_path;

  const act = async (kind) => {
    setBusy(true);
    try {
      if (kind === "approve") {
        const res = await approvePhotoSubmission(sub.id, {
          notes: notes || undefined,
          set_canonical: setCanonical,
          queue_ingredient_ocr: hasIngredients && queueOcr,
        });
        const extra = res?.ingredient_ocr_queued ? " · OCR queued" : "";
        toast.success(`Approved${setCanonical ? " · canonical updated" : ""}${extra}`);
      } else {
        await rejectPhotoSubmission(sub.id, { notes: notes || undefined });
        toast.success("Rejected");
      }
      onAction(sub.id, kind === "approve" ? "APPROVED" : "REJECTED");
    } catch (e) {
      toast.error(formatApiError(e, `${kind} failed`));
    } finally {
      setBusy(false);
    }
  };

  return (
    <article
      data-testid={`photo-submission-card-${sub.id}`}
      className="rounded-2xl border border-stone-200 bg-white shadow-sm overflow-hidden"
    >
      <div className="flex items-center gap-3 px-5 py-3.5 border-b border-stone-100 bg-stone-50/70 flex-wrap">
        <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${tone}`}>
          {sub.status}
        </span>
        {sub.had_image && (
          <span className="inline-flex items-center rounded-full border border-stone-300 bg-white px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-stone-600">
            Replacing existing
          </span>
        )}
        {hasIngredients && (
          <span className="inline-flex items-center rounded-full border border-stone-300 bg-white px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-stone-600">
            +Ingredients
          </span>
        )}
        <span className="ml-auto text-[11px] text-stone-400 font-mono truncate max-w-[200px]">
          {sub.id}
        </span>
      </div>

      <div className="px-5 py-4 space-y-4">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-stone-500">Product</div>
          <div className="font-semibold text-stone-900 text-sm mt-0.5 truncate">
            {sub.product_brand || "?"} · {sub.product_name || sub.product_id}
          </div>
          {sub.product_slug && (
            <Link
              to={`/p/${sub.product_slug}`}
              target="_blank"
              rel="noreferrer"
              className="text-[11.5px] text-stone-500 underline hover:text-stone-900"
            >
              /p/{sub.product_slug} ↗
            </Link>
          )}
        </div>

        <div className={`grid ${hasIngredients ? "sm:grid-cols-2" : "sm:grid-cols-1"} gap-3`}>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-stone-500 mb-1">Front</div>
            <AuthedThumbnail
              submissionId={sub.id}
              kind="front"
              status={sub.status}
              alt={`Front of ${sub.product_name || ""}`}
            />
          </div>
          {hasIngredients && (
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-stone-500 mb-1">Ingredients</div>
              <AuthedThumbnail
                submissionId={sub.id}
                kind="ingredients"
                status={sub.status}
                alt={`Ingredients label of ${sub.product_name || ""}`}
              />
            </div>
          )}
        </div>

        {sub.note && (
          <div className="rounded-xl border border-stone-200 bg-stone-50 p-2.5">
            <div className="text-[10px] uppercase tracking-wider font-bold text-stone-500 mb-0.5">Submitter note</div>
            <p className="text-[12.5px] text-stone-700">{sub.note}</p>
          </div>
        )}

        <div className="text-[11.5px] text-stone-500 flex items-center gap-2 flex-wrap">
          {sub.reporter_email && <span>From: {sub.reporter_email}</span>}
          <span>·</span>
          <span>{(sub.submitted_at || "").slice(0, 19).replace("T", " ")}</span>
        </div>

        {sub.resolution_notes && !isPending && (
          <div className="rounded-xl border border-stone-200 bg-stone-50 p-2.5">
            <div className="text-[10px] uppercase tracking-wider font-bold text-stone-500 mb-0.5">Reviewer notes</div>
            <p className="text-[12.5px] text-stone-700">{sub.resolution_notes}</p>
          </div>
        )}
      </div>

      {isPending && (
        <>
          <div className="border-t border-stone-100 bg-stone-50 px-5 py-3 space-y-2">
            <label className="block">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">
                Reviewer notes (optional)
              </span>
              <input
                data-testid={`photo-submission-notes-${sub.id}`}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. Clear shot, brand label visible."
                className="mt-1 w-full h-9 px-2 rounded border border-stone-300 bg-white text-sm"
              />
            </label>
            <label className="inline-flex items-center gap-2 text-[12px] text-stone-700 cursor-pointer">
              <input
                type="checkbox"
                checked={setCanonical}
                onChange={(e) => setSetCanonical(e.target.checked)}
                data-testid={`photo-submission-set-canonical-${sub.id}`}
                className="h-3.5 w-3.5 cursor-pointer"
              />
              Use as canonical product image (writes <code className="font-mono">image_url</code>)
            </label>
            {hasIngredients && (
              <label className="inline-flex items-center gap-2 text-[12px] text-stone-700 cursor-pointer ml-4">
                <input
                  type="checkbox"
                  checked={queueOcr}
                  onChange={(e) => setQueueOcr(e.target.checked)}
                  data-testid={`photo-submission-queue-ocr-${sub.id}`}
                  className="h-3.5 w-3.5 cursor-pointer"
                />
                Auto-OCR ingredients label → ingredient submission
              </label>
            )}
          </div>
          <div className="flex items-center gap-2 px-5 py-3 border-t border-stone-100 flex-wrap">
            <Button
              onClick={() => act("approve")}
              disabled={busy}
              data-testid={`photo-submission-approve-${sub.id}`}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}
              Approve & apply
            </Button>
            <Button
              variant="ghost"
              onClick={() => act("reject")}
              disabled={busy}
              data-testid={`photo-submission-reject-${sub.id}`}
              className="text-red-600 hover:bg-red-50"
            >
              <XCircle className="h-4 w-4 mr-1.5" /> Reject
            </Button>
          </div>
        </>
      )}
    </article>
  );
}

export default function AdminPhotoSubmissions() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("PENDING");

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchAdminPhotoSubmissions(statusFilter);
      setData(res);
    } catch (e) {
      toast.error(formatApiError(e, "Failed to load photo submissions"));
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { reload(); }, [reload]);

  const onAction = (id, newStatus) => {
    setData((prev) => prev ? {
      ...prev,
      items: prev.items.map((s) => s.id === id ? { ...s, status: newStatus } : s),
    } : prev);
    setTimeout(reload, 200);
  };

  const summary = data?.summary || { pending: 0, approved: 0, rejected: 0 };

  return (
    <main
      data-testid="admin-photo-submissions-page"
      className="min-h-screen bg-[#fdf8f3] px-4 sm:px-6 py-10 sm:py-12"
    >
      <div className="mx-auto max-w-5xl">
        <AdminHeader />

        <header className="mt-5 mb-6 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-stone-900 flex items-center gap-2">
              <Camera className="h-7 w-7 text-stone-700" />
              Product Photo Submissions
            </h1>
            <p className="mt-1.5 text-sm text-stone-600 max-w-xl leading-relaxed">
              Community-uploaded front + ingredients photos for products
              missing imagery. Approve to promote the front image to canonical
              and (optionally) auto-queue ingredient OCR.
            </p>
          </div>
          <Link
            to="/admin/dashboard"
            className="inline-flex items-center gap-1.5 text-xs text-stone-500 hover:text-stone-900"
          >
            <ShieldCheck className="h-3.5 w-3.5" /> Admin
          </Link>
        </header>

        <div className="grid grid-cols-3 gap-2 mb-5">
          <StatTile label="Pending"  value={summary.pending}  tone="amber" />
          <StatTile label="Approved" value={summary.approved} tone="green" />
          <StatTile label="Rejected" value={summary.rejected} tone="red" />
        </div>

        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <span className="text-xs text-stone-500">Filter:</span>
          {["PENDING", "APPROVED", "REJECTED"].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              data-testid={`photo-submissions-filter-${s.toLowerCase()}`}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                statusFilter === s
                  ? "bg-stone-900 text-white border-stone-900"
                  : "bg-white text-stone-700 border-stone-300 hover:border-stone-400"
              }`}
            >
              {s}
            </button>
          ))}
          <button
            onClick={reload}
            data-testid="photo-submissions-refresh"
            className="ml-auto text-xs text-stone-500 hover:text-stone-900 px-2 py-1.5"
          >
            Refresh
          </button>
        </div>

        {loading ? (
          <div className="rounded-2xl border border-stone-200 bg-white p-10 text-center text-stone-400">
            <Loader2 className="h-5 w-5 animate-spin mx-auto" />
          </div>
        ) : (data?.items?.length ?? 0) === 0 ? (
          <div
            data-testid="photo-submissions-empty"
            className="rounded-2xl border border-dashed border-stone-200 bg-white p-12 text-center"
          >
            <Camera className="h-10 w-10 text-stone-400 mx-auto" />
            <p className="mt-3 text-sm font-semibold text-stone-700">Nothing in this bucket.</p>
            <p className="mt-1 text-xs text-stone-500">
              {statusFilter === "PENDING"
                ? "No photo submissions awaiting review."
                : "Switch the filter above to see other rows."}
            </p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="photo-submissions-list">
            {data.items.map((sub) => (
              <PhotoSubmissionCard key={sub.id} sub={sub} onAction={onAction} />
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
